<template>
  <v-app>
    <div v-if="store.isLoading" class="loading-box">
      <v-progress-circular color="primary" indeterminate :size="50" :width="8" />
    </div>
    <v-app-bar v-if="!store.isPrinting" theme="dark">
      <FusionAppBar
        :logoSrc="store.logoSrc"
        :logoWidth="'206'"
        :hasDrawer="store.hasDrawer"
        :isLoading="store.isLoading"
        :user="store.auth0User"
        @drawerClicked="store.drawerToggle"
        data-test-id="appBar"
      >
        <template v-slot:burger>
          <span v-if="store.showBurger" class="application-burger">
            <v-app-bar-nav-icon
              @click.stop="store.drawerToggle"
              class="mx-0 text"
              size="x-large"
              :color="theme.current.value.colors['on-surface']"
              data-test-id="hamburgerButton"
            />
          </span>
          <span v-else class="no-application-burger" />
        </template>

        <template v-slot:left-toolbar>
          <div class="application-title">
            <h2>{{ t('app.title') }}</h2>
          </div>
        </template>
        <template v-slot:right-toolbar>
          <DatasetStatus
            v-if="datasetsStore.activeDataset"
            :isSandbox="datasetsStore.activeDataset.isSandbox"
          />
          <FusionAuthenticator :hideUserProfile="true" />
        </template>
      </FusionAppBar>
    </v-app-bar>
    <v-navigation-drawer
      v-if="store.hasDrawer && !store.isPrinting"
      v-model="store.drawer.open"
      :color="Colors.alternateBackground"
      :permanent="store.drawerPermanent"
      :rail="false"
      rail-width="65"
      width="240"
    >
      <FusionSideBar
        :routes="store.routes"
        :drawerMini="false"
        data-test-id="sideBar"
      ></FusionSideBar>

      <template v-if="store.drawer.open" v-slot:append>
        <ButtonIcon
          style="margin-left: 0.25rem"
          @click="store.openFeedbackDialog"
          :text="t('buttons.openFeedback')"
          icon="mdi-bullhorn"
        />
        <v-divider class="divider" />
        <div v-if="!store.isPrinting" class="footer">
          <span>
            &copy; {{ new Date().getFullYear() }}
            {{ t('app.copyright') }}
          </span>
          <span>v{{ store.appVersion }}</span>
        </div>
      </template>

      <PrimarySelect
        v-if="datasetsStore.enabledItems.length > 0"
        class="selector"
        v-model="settingsStore.activeDatasetId"
        :label="t('labels.activeDatasetId')"
        :items="datasetsStore.enabledOptions"
        @update:model-value="refreshPage"
      ></PrimarySelect>
      <!--
      <PrimarySelect
        class="selector"
        v-model="settingsStore.localeCode"
        :label="t('labels.activeLanguage')"
        :items="settingsStore.languageOptions"
      ></PrimarySelect>
      -->
    </v-navigation-drawer>
    <v-main>
      <v-container class="main-container ma-0 pa-0" fluid>
        <router-view v-if="store.isAuthenticated" />
        <v-row v-else>
          <v-col cols="12" xs="12" sm="10" md="8" lg="6" xl="4" xxl="4">
            <ActivateContent
              v-if="store.loadStatus === LoadStatus.NoSubscription"
              :activation-code="store.activationCode"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.AuthTimeout"
              :header="t(`headers.content.${store.loadStatus}`)"
              :message="t(`text.content.${store.loadStatus}`)"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.UnverifiedEmail"
              :force-refresh="true"
              :header="t(`headers.content.${store.loadStatus}`)"
              :message="t(`text.content.${store.loadStatus}`)"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.NotAuthorizedForAuth0"
              :header="t('headers.content.notAuthorized')"
              :message="t('text.content.notAuthorized')"
            >
              {{ t('text.content.notAuthorizedNote') }}
              <span class="email">{{ store.auth0User.email }}</span>
              <h4 style="margin-top: 1rem">{{ t('labels.programAdmins') }}:</h4>
              <v-data-table
                :headers="store.adminHeaders"
                :items="store.admins"
                :items-per-page="store.itemsPerPage"
                :items-per-page-options="settingsStore.itemsPerPageOptions"
              >
                <template v-slot:item.email="{ item }">
                  <a class="email-link" :href="getMailLink(item)">
                    <v-icon
                      icon="mdi-email"
                      v-tooltip="{ openOnClick: true, text: item.email }"
                      size="small"
                    />
                  </a>
                  <v-icon
                    style="margin-left: 0.5rem"
                    icon="mdi-content-copy"
                    v-tooltip="{ openOnClick: true, text: item.email }"
                    size="small"
                    @click="copyToClipboard(item)"
                  />
                </template>
              </v-data-table>
            </AuthContent>
            <AuthContent
              v-if="store.loadStatus === LoadStatus.ErrorInitialization"
              :header="t(`headers.content.${store.loadStatus}`)"
              :message="t(`text.content.${store.loadStatus}`)"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.ErrorLoadingDatasets"
              :header="t(`headers.content.${store.loadStatus}`)"
              :message="t(`text.content.${store.loadStatus}`)"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.ErrorLoadingSettings"
              :header="t(`headers.content.${store.loadStatus}`)"
              :message="t(`text.content.${store.loadStatus}`)"
            />
            <AuthContent
              v-if="store.loadStatus === LoadStatus.NotAuthorizedForScheduler"
              :header="t('headers.content.notAuthorized')"
              :message="t(`text.content.${store.loadStatus}`)"
            />
          </v-col>
        </v-row>
      </v-container>
      <FusionSnackbar />
    </v-main>

    <DialogDeviceAlert v-if="!store.isPrinting" />
    <FusionFeedbackDialog
      v-if="!store.isPrinting"
      :open="store.showFeedbackDialog"
      @close="store.closeFeedbackDialog"
    ></FusionFeedbackDialog>
  </v-app>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import Colors from '@/styles/_colors.module.scss'
import { getAuthInstance } from '@fusion/auth-v3'
import { onBeforeMount, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { ThemeInstance } from 'vuetify'
import { useTheme } from 'vuetify'
import DatasetStatus from './components/DatasetStatus.vue'
import DialogDeviceAlert from './components/dialogs/DialogDeviceAlert.vue'
import { LoadStatus } from './enums/LoadStatus'
import { MessageType } from './enums/MessageType'
import type { IDatasetStore } from './interfaces/api/IDatasetStore'
import { AdminRecord } from './models/AdminRecord'
import { useDatasetsStore } from './stores/db/DatasetsStore'
import { useAppStore } from './stores/ui/AppStore'
import { useSnackbarStore } from './stores/ui/SnackbarStore'

const datasetsStore = useDatasetsStore() as IDatasetStore
const router = useRouter()
const settingsStore = useSettingsStore()
const snackbarStore = useSnackbarStore()
const store = useAppStore()
const { t } = useI18n({ useScope: 'global' })
const theme: ThemeInstance = useTheme()

// PROPS & EMITS

// REACTIVE VARIABLES

// LIFECYCLE HOOKS
onBeforeMount(async () => {
  await router.isReady()
  if (store.isSelfSchedule) {
    await store.loadSelfScheduler()
  } else {
    await store.loadApplication()
  }
})

onMounted(async () => {
  const auth = getAuthInstance()
  try {
    await auth.getTokenSilently()
  } catch (e: any) {
    console.warn('Silent Auth failed on mount', e.message)
  }
})

router.beforeEach((to) => {
  document.title = `${t('app.title')} - ${t(to.meta.title)}`
})

// COMPUTED PROPERTIES

// FUNCTIONS
const getMailLink = (item: AdminRecord) => {
  return `mailto:${item.email}?subject=${encodeURIComponent(t('text.emailSubject'))}&body=${encodeURIComponent(t('text.emailBody', { email: store.auth0User.email }))}`
}

const copyToClipboard = (item: AdminRecord) => {
  if (item?.email) {
    navigator.clipboard.writeText(item.email)
    snackbarStore.addMessage(t('text.copiedToClipboard'), MessageType.Info)
  }
}

const refreshPage = () => {
  window.location.reload()
}
</script>

<style lang="scss" scoped>
.application-burger {
  background-color: #fff;
  height: 100%;
  align-items: center;
  padding: 0;
}

.divider {
  margin-bottom: 0;
  padding-bottom: 0;
}

.email {
  font-style: italic;
  font-weight: bold;
}

.footer {
  opacity: 0.3;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  max-height: 74px;
  white-space: nowrap;
}

.main-container {
  position: relative;
  left: 0;
  right: 0;
}

.selector {
  padding: 1rem 0.75rem 0 0.75rem;
}

.application-title {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  @media (max-width: 830px) {
    display: none;
  }

  h2 {
    margin: 0;
    padding: 0;
  }
}

.email-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.no-application-burger {
  background-color: #fff;
  align-items: center;
  padding: 0;
}

.loading-box {
  z-index: 1000000000;
  text-align: center;
  height: 100px;
  width: 100px;
  margin: auto;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.navigation-drawer {
  @media print {
    display: none;
    width: 0;
  }
}
</style>

<style lang="scss" src="@/styles/_styles.scss" />
